import React, { useState, useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import "chartjs-adapter-date-fns";
import axios from "axios";
import "./BacktestPortfolioPlot.css";
import { Link } from "react-router-dom";

Chart.register(...registerables);

const BacktestPortfolioPlot = ({ ticker, setHasBacktesting }) => {
  const [portfolioData, setPortfolioData] = useState([]);
  const [metricsData, setMetricsData] = useState(null);
  const [error, setError] = useState(null);
  const chartRef = useRef();
  const chartInstance = useRef(null);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!ticker) return;

      try {
        const baseURL =
          process.env.REACT_APP_ENVIRONMENT_TYPE === "DEV"
            ? "http://127.0.0.1:5000"
            : "https://flask-backend-52245432644.us-central1.run.app";

        const [portfolioResponse, metricsResponse] = await Promise.all([
          axios.get(`${baseURL}/get-backtest-portfolio?ticker=${ticker}`),
          axios.get(`${baseURL}/get-backtest-metrics?ticker=${ticker}`),
        ]);

        setPortfolioData(portfolioResponse.data);
        setMetricsData(metricsResponse.data);
        setError(null);
        setHasBacktesting(true);
      } catch (err) {
        setError("Error fetching data. Please try again.");
        setPortfolioData([]);
        setMetricsData(null);
        setHasBacktesting(false);
      }
    };

    fetchData();
  }, [ticker]);

  useEffect(() => {
    if (portfolioData.length === 0) return;

    // Log the data range
    console.log("Portfolio Data Range:", {
      first: portfolioData[0].date,
      last: portfolioData[portfolioData.length - 1].date,
      totalPoints: portfolioData.length,
    });

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext("2d");

    const datasets = [
      {
        label: "Our Predictions",
        data: portfolioData.map((item) => ({
          x: new Date(
            new Date(item.date).getTime() +
              new Date(item.date).getTimezoneOffset() * 60000
          ),
          y: item.strategy_portfolio_value,
        })),
        borderColor: "#00B8A9",
        borderWidth: 2,
        pointRadius: 0,
        fill: false,
      },
      {
        label: "Buy-and-Hold",
        data: portfolioData.map((item) => ({
          x: new Date(
            new Date(item.date).getTime() +
              new Date(item.date).getTimezoneOffset() * 60000
          ),
          y: item.buy_hold_portfolio_value,
        })),
        borderColor: "#457B9D",
        borderWidth: 2,
        pointRadius: 0,
        fill: false,
      },
    ];

    // Add logging to verify date range
    console.log("Date range:", {
      firstDate: new Date(portfolioData[0].date),
      lastDate: new Date(portfolioData[portfolioData.length - 1].date),
      adjustedFirstDate: new Date(
        new Date(portfolioData[0].date).getTime() +
          new Date(portfolioData[0].date).getTimezoneOffset() * 60000
      ),
      adjustedLastDate: new Date(
        new Date(portfolioData[portfolioData.length - 1].date).getTime() +
          new Date(
            portfolioData[portfolioData.length - 1].date
          ).getTimezoneOffset() *
            60000
      ),
    });

    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: "index",
        },
        plugins: {
          legend: {
            position: "top",
            align: "start",
            labels: {
              usePointStyle: false,
              boxWidth: 30,
              boxHeight: 1,
              padding: 20,
              font: {
                family: "Inter, sans-serif",
                size: 13,
                weight: "400",
              },
              generateLabels: function (chart) {
                const datasets = chart.data.datasets;
                return datasets.map((dataset, i) => ({
                  text: dataset.label,
                  fillStyle: "transparent",
                  strokeStyle: dataset.borderColor,
                  lineWidth: 2,
                  hidden: !chart.isDatasetVisible(i),
                  lineDash: dataset.borderDash || [],
                  lineCap: "butt",
                  lineJoin: "miter",
                  index: i,
                }));
              },
            },
          },
          tooltip: {
            enabled: true,
            mode: "index",
            intersect: false,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            padding: 10,
            titleFont: {
              size: 14,
              weight: "normal",
            },
            bodyFont: {
              size: 14,
            },
            callbacks: {
              title: function (context) {
                const date = new Date(context[0].parsed.x);
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                });
              },
              label: function (context) {
                const lineSymbol = "─";
                let label = context.dataset.label || "";
                if (label) {
                  label = `${lineSymbol} ${label}: `;
                }
                if (context.parsed.y !== null) {
                  label += new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(context.parsed.y);
                }
                return label;
              },
              labelTextColor: function (context) {
                return context.dataset.borderColor;
              },
            },
            displayColors: false,
          },
        },
        scales: {
          x: {
            type: "time",
            time: {
              unit: "day",
              displayFormats: {
                day: "MMM dd",
              },
            },
            grid: {
              display: true,
              color: "#E9ECEF",
            },
            ticks: {
              font: {
                family: "'Inter', sans-serif",
                size: 11,
              },
              maxRotation: 45,
              minRotation: 45,
            },
          },
          y: {
            grid: {
              display: true,
              color: "#E9ECEF",
            },
            ticks: {
              callback: function (value) {
                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  notation: "compact",
                  compactDisplay: "short",
                }).format(value);
              },
              font: {
                family: "'Inter', sans-serif",
                size: 11,
              },
            },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [portfolioData]);

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatMaxDrawdown = (value) => {
    return `${Math.round(value * 100)}%`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatNumber = (value) => {
    return Number(value).toFixed(2);
  };

  return portfolioData.length > 0 ? (
    <div className="backtest-portfolio-plot" style={{ marginBottom: "20px" }}>
      <h3
        style={{
          fontFamily: "Inter, sans-serif",
          color: "#1D3557",
          marginBottom: "15px",
          textAlign: "left",
          borderBottom: "1px solid rgba(0, 0, 0, 0.25)",
          padding: "20px 0px 20px 20px",
        }}
      >
        Backtesting Results
      </h3>

      {error && <div className="error-message">{error}</div>}

      <div
        className="content-container"
        style={{
          display: "flex",
          gap: "20px",
          alignItems: "center",
          flexDirection: "column",
          maxWidth: "1200px",
          margin: "0 auto",
          justifyContent: "center",
        }}
      >
        <div
          className="chart-container"
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            padding: "15px",
            borderBottom: "1px solid rgba(0, 0, 0, 0.15)",
          }}
        >
          <p
            style={{
              fontSize: "16px",
              fontFamily: "Inter, sans-serif",
              color: "#1D3557",
              fontWeight: 600,
            }}
          >
            Growth of Hypothetical $10,000*
          </p>
          <div style={{ height: "300px" }}>
            <canvas
              ref={chartRef}
              style={{ width: "100%", height: "100%" }}
            ></canvas>
          </div>
          <span
            style={{
              fontSize: "13px",
              color: "rgba(0, 0, 0, 0.5)",
              fontFamily: "Inter, sans-serif",
              marginTop: "10px",
            }}
          >
            * Performance data shown represents past performance and is no
            guarantee of future results. Investment return and principal value
            will fluctuate, so you may have a gain or loss when shares are sold.
          </span>
        </div>

        {metricsData && (
          <div
            className="performance-section"
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "white",
              transition: "all 0.3s ease",
              borderRadius: 8,
            }}
          >
            <table className="performance-table">
              <colgroup>
                <col style={{ width: "25%" }} />
                <col style={{ width: "25%" }} />
                <col style={{ width: "25%" }} />
                <col style={{ width: "25%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      textAlign: "left",
                      fontFamily: "Inter, sans-serif",
                      paddingBottom: "10px",
                      paddingRight: "10px",
                    }}
                    colSpan={2}
                  >
                    Our Predictions
                  </th>
                  <th
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      textAlign: "left",
                      fontFamily: "Inter, sans-serif",
                      paddingBottom: "10px",
                      paddingLeft: "10px",
                    }}
                    colSpan={2}
                  >
                    Buy/Hold
                  </th>
                </tr>
              </thead>
              <tbody style={{ paddingBottom: "20px" }}>
                <tr>
                  <td>Returns</td>
                  <td>{formatPercentage(metricsData.total_pnl_percent)}</td>
                  <td>Returns</td>
                  <td>{formatPercentage(metricsData.buy_and_hold_return)}</td>
                </tr>
                <tr>
                  <td>Sharpe Ratio</td>
                  <td>{formatNumber(metricsData.strategy_sharpe)}</td>
                  <td>Sharpe Ratio</td>
                  <td>{formatNumber(metricsData.buy_hold_sharpe)}</td>
                </tr>
                <tr>
                  <td>Average Win</td>
                  <td>{formatCurrency(metricsData.avg_win)}</td>
                  <td>Average Win</td>
                  <td>N/A</td>
                </tr>
                <tr>
                  <td>Average Loss</td>
                  <td>{formatCurrency(metricsData.avg_loss)}</td>
                  <td>Average Loss</td>
                  <td>N/A</td>
                </tr>
                <tr>
                  <td>Max Drawdown</td>
                  <td>{formatMaxDrawdown(metricsData.max_drawdown)}</td>
                  <td>Max Drawdown</td>
                  <td>N/A</td>
                </tr>
                <tr>
                  <td>Win Rate</td>
                  <td>{formatPercentage(metricsData.win_rate)}</td>
                  <td>Win Rate</td>
                  <td>N/A</td>
                </tr>
              </tbody>
            </table>
            <Link
              to="/dashboard/ai-picks"
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                gap: 4,
                width: "100%",
                justifyContent: "center",
                paddingTop: "10px",
                paddingBottom: "20px",
                fontFamily: "Inter, sans-serif",
                color: "#1d3557",
                fontStyle: "italic",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              <p
                style={{
                  textAlign: "center",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                See
              </p>
              <span
                style={{
                  textDecoration: "underline",
                  textAlign: "center",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Top AI Picks
              </span>
              <p
                style={{
                  textAlign: "center",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                — our best performing tickers.
              </p>
            </Link>
          </div>
        )}
      </div>
    </div>
  ) : null;
};

export default BacktestPortfolioPlot;
